<!--
Renders a small "rich text" content model as real DOM elements instead of building
and injecting an HTML string via v-html — so untrusted interpolated text (a Slack name,
a channel name, free text someone typed) stays safe through Vue's auto-escaping `{{ }}`,
with no sanitizing/escaping required at the call site. See SEC-105.

`modelValue` is either a plain string (rendered as-is, for the common case of one plain-text value
with no formatting) or an array of items, each one either a bare string (shorthand for { text: string }) or:
  { text, class }
    Plain text, rendered as `<span :class="class">{{ text }}</span>`. `class` is optional; when set
    it should always be a hardcoded literal (e.g. 'bold', 'gray'), never derived from untrusted data.
  { type: 'break' }
    A line break (`<br>`).
  { type: 'emoji', name }
    An emoji shortcode (e.g. name: 'tada'), rendered via the v-emoji directive.
  { type: 'link', text, href, class, target }
    A link, rendered as `<a class="link" :class="class" :href="href" :target="target">{{ text }}</a>`.
    `href` should be a fixed/trusted URL, not derived from untrusted data. `target` is optional (e.g. '_blank').
-->
<template>
  <div class="t">
    <template v-for="(section, idx) in normalizedSections">
      <span v-if="section.type === 'emoji'" :key="`emoji-${idx}`" v-emoji="`:${section.name}:`" />
      <br v-else-if="section.type === 'break'" :key="`break-${idx}`">
      <a v-else-if="section.type === 'link'" :key="`link-${idx}`" class="link" :class="section.class" :href="section.href" :target="section.target">{{ section.text }}</a>
      <span v-else :key="idx" :class="section.class">{{ section.text }}</span>
    </template>
  </div>
</template>

<script setup>
  import { computed } from 'vue';

  const props = defineProps({
    modelValue: { type: [Array, String], required: true },
  });

  const normalizedSections = computed(() => {
    const sections = typeof props.modelValue === 'string' ? [props.modelValue] : props.modelValue;
    return sections.map(section => (typeof section === 'string' ? { text: section } : section));
  });
</script>
